import { gql } from '@apollo/client';
import { chakra, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { Power, SettingWarningSeverity } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { useRecommendedSettings } from '../../useRecommendedSettings';
import { mapStringToPowerEnum } from '../../utils';
import {
  UpdatePowerMutation,
  UsePowerToggle_DisplayFragment,
  useUpdatePowerMutation,
} from './__generated__/usePowerToggle.graphql';

interface Props {
  display: UsePowerToggle_DisplayFragment;
}

export function usePowerToggle({ display }: Props) {
  const currentPower = useMemo(() => display.power?.desired ?? display.power?.reported, [display]);
  const analytics = useAnalyticsReporter();
  const { t } = useTranslation();
  const { getSettingsState } = useRecommendedSettings();
  const powerApplyAction = useDestructiveAction<string>({
    title: () => <>{t('Warning')}</>,
    message: () => (
      <>
        {t('powerText')}
        <chakra.span fontSize="md" fontWeight="semibold">
          {t('powerTextBold')}
        </chakra.span>
      </>
    ),
    onConfirm: async (data) => {
      await handleUpdatePower(data);
    },
    confirmLabel: t('applyAnyway'),
    variant: 'warning',
    notice: undefined,
  });

  const toast = useToast();
  const [updatePower] = useUpdatePowerMutation();

  const handleUpdatePower = useCallback(
    async (value: string) => {
      analytics.track('displaySettingUpdate', { group: 'power', changeItem: 'power' });

      try {
        const power = mapStringToPowerEnum(value);
        const optimisticResponse: UpdatePowerMutation = {
          __typename: 'Mutation',
          displayUpdatePower: {
            __typename: 'DisplayUpdatePowerPayload',
            display: _.merge(_.cloneDeep(display), {
              power: {
                desired: power,
              },
            }),
          },
        };

        await updatePower({
          variables: {
            input: {
              id: display.id,
              power,
            },
          },
          optimisticResponse,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('powerUpdateErr'),
          description: fromError(err, 'UpdatePower'),
        });
      }
    },
    [analytics, display, updatePower, toast, t],
  );

  const handleChange = useCallback(async () => {
    const value = currentPower === Power.On ? Power.Standby : Power.On;
    if (value === Power.Standby) {
      const settings = getSettingsState(display);
      const hasHighSeverityWarning =
        settings.kind === 'not_recommended' &&
        settings.highestSeverity === SettingWarningSeverity.High;

      if (hasHighSeverityWarning) {
        await powerApplyAction.askConfirmation(value);
        return;
      }
    }
    await handleUpdatePower(value);
  }, [currentPower, display, getSettingsState, handleUpdatePower, powerApplyAction]);

  return {
    currentPower,
    confirmationNode: powerApplyAction.confirmationNode,
    handleChange,
  };
}

usePowerToggle.graphql = {
  fragments: {
    usePowerToggle_display: gql`
      fragment usePowerToggle_display on Display {
        id
        power {
          reported
          reportedAt
          desired
        }
        ...UseRecommendedSettings_display
      }
    `,
  },
  mutations: {
    UpdatePower: gql`
      mutation UpdatePower($input: DisplayUpdatePowerInput!) {
        displayUpdatePower(input: $input) {
          display {
            id
            power {
              reported
              reportedAt
              desired
            }
            ...UseRecommendedSettings_display
          }
        }
      }
    `,
  },
};
